import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';

import { useParams } from 'react-router-dom';

import { DialogContent } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';

import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';

import {
    AdditionalDocStatusEnum,
    closeChangeReleaseStatusDialog,
    getAdditionalDocumentStatuses,
} from '../../../../redux/project/documents/sliceProjectAdditionalDocumentChangeReleaseStatus';
import ChangeAdditionalDocumentStatusDialogActions from './_parts/ChangeAdditionalDocumentStatusDialogActions';
import ChangeAdditionalDocumentStatusContent from './_parts/ChangeAdditionalDocumentStatusContent';
import { getAdditionalDocs } from '../../../../redux/project/documents/sliceProjectAdditionalDocuments';
import { clearProjectDocumentsInfo } from '../../../../redux/project/documents/sliceProjectDocuments';

const initialMainState: IChangeAdditionalDocumentStatusMainState = {
    command: null,
    comment: '',
};

const initialErrorState: IChangeAdditionalDocumentStatusErrorState = {
    command: false,
};

export default () => {
    const { t } = useTranslation('translations', {
        keyPrefix: 'changeAdditionalDocumentStatusDialog',
    });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const { replicaId } = useParams();

    const { isDialogOpen, isStatusChanged, loadingStatusOptions, referencedMainDocId } =
        useSelector((_store: IAppReduxStore) => _store.projectDocumentChangeReleaseStatus);

    const { data: documents, loading: loadingMainDocument } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );
    const fields = useSelector((store: IAppReduxStore) => store.projectAdditionalDocs.fields.data);
    const fieldSchema = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.schema.data,
    );
    useEffect(() => {
        if (isDialogOpen) {
            dispatch(getAdditionalDocumentStatuses({ replicaId: replicaId }));
        }
    }, [isDialogOpen]);

    useEffect(() => {
        if (isStatusChanged) {
            const mainDocument = documents[0];

            dispatch(
                getAdditionalDocs({
                    replicaId,
                    universalId: referencedMainDocId,
                    noteId: mainDocument.NoteId,
                    fields: [].concat(
                        ...fieldSchema.map(uf => fields.find(x => x.field === uf)?.fields ?? []),
                    ),
                }),
            );

            dispatch(clearProjectDocumentsInfo());
            enqueueSnackbar(t('toastSuccess'), {
                severity: ToastSeverity.Success,
            });
            onClose();
        }
    }, [isStatusChanged]);

    const [mainState, setMainState] =
        useState<IChangeAdditionalDocumentStatusMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<IChangeAdditionalDocumentStatusErrorState>(initialErrorState);

    const handleChange = ({
        field,
        value,
    }: {
        field: string;
        value: string | AdditionalDocStatusEnum;
    }) => {
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
        if (
            value === AdditionalDocStatusEnum.Release ||
            value === AdditionalDocStatusEnum.RevokeRelease
        ) {
            setMainState(prevState => ({
                ...prevState,
                comment: '',
            }));
        }
        setShowErrorState(prevState => ({
            ...prevState,
            [field]: false,
        }));
    };

    const resetState = () => {
        setShowErrorState({ ...initialErrorState });
        setMainState({ ...initialMainState });
    };

    const onClose = () => {
        resetState();
        dispatch(closeChangeReleaseStatusDialog());
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingStatusOptions || loadingMainDocument}
                    documentSelection={null}
                    main={
                        <ChangeAdditionalDocumentStatusContent
                            mainState={mainState}
                            handleChange={handleChange}
                            showErrorState={showErrorState}
                        />
                    }
                />
            </DialogContent>

            <ChangeAdditionalDocumentStatusDialogActions
                setShowErrorState={setShowErrorState}
                mainState={mainState}
                onClose={onClose}
            />
        </DialogFrame>
    );
};

export interface IChangeAdditionalDocumentStatusMainState {
    command: AdditionalDocStatusEnum;
    comment: string;
}

export interface IChangeAdditionalDocumentStatusErrorState {
    command: boolean;
}
